// @flow strict

// Cross-boundary open/close control for the sidebar (GF-2954).
//
// Historically this was a React-backed singleton: SideBar/index.jsx registered
// its useState pair via setControls, and the external consumers called through
// it. The extraction moves the state to the `sidebar` Stimulus controller, so
// this module becomes a *bridge* rather than a holder — per island-contract
// rule 3, cross-boundary communication goes through a namespaced CustomEvent,
// not a direct DOM rewrite or a shared React handle.
//
// The module path and exports are deliberately unchanged so the three external
// consumers (pages/GovernanceMeeting, pages/TacticalMeeting, ui/SectionItem)
// need no edit.
//
// Both worlds are supported on purpose. While the React sidebar and the HAML
// sidebar can both exist (the ticket's "medium shim budget" dual-render
// window), reads prefer the server-rendered DOM and fall back to whatever React
// registered, then to the persisted value. That ordering means this file is
// correct before, during, and after the view-layer swap.

const TOGGLE_EVENT = 'sidebar:toggle'
const STORAGE_KEY = 'side-bar-is-open'
const ROOT_SELECTOR = '[data-sidebar-open]'

type Controls = $ReadOnly<{
  isSidebarOpen: () => boolean,
  toggleSidebar: () => void,
}>

// Only ever populated by the legacy React sidebar. Stays null once it is gone.
let registeredControls: Controls | null = null

// Non-null only when the Stimulus-controlled sidebar is in the DOM.
function readFromDom(): boolean | null {
  const root = document.querySelector(ROOT_SELECTOR)
  if (!root)
    return null

  return root.getAttribute('data-sidebar-open') === 'true'
}

function readFromStorage(): boolean | null {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null')
    return typeof stored === 'boolean' ? stored : null
  } catch (e) {
    return null
  }
}

function setControls(controls: Controls) {
  registeredControls = controls
}

function isSidebarOpen(): boolean {
  const fromDom = readFromDom()
  if (fromDom !== null)
    return fromDom

  if (registeredControls)
    return registeredControls.isSidebarOpen()

  const fromStorage = readFromStorage()
  if (fromStorage !== null)
    return fromStorage

  // Matches the React default: usePersistentState(true, 'side-bar-is-open').
  return true
}

function toggleSidebar() {
  // The Stimulus controller owns the state whenever it is mounted; it listens
  // for this on window and is the only thing that writes the class/attribute.
  if (readFromDom() !== null) {
    window.dispatchEvent(new CustomEvent(TOGGLE_EVENT))
    return
  }

  if (registeredControls)
    registeredControls.toggleSidebar()
}

export {
  isSidebarOpen,
  toggleSidebar,
  setControls,
}
